import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Chip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tabs,
    Tab,
    Tooltip,
    IconButton,
    Divider,
    CircularProgress,
} from "@mui/material";
import {
    CheckCircle,
    Cancel,
    Visibility,
    Refresh,
    ShoppingCart,
    Person,
    LocalShipping,
    Payment,
    DeleteSweep,
    PlaylistAddCheck,
    Sync,
    Insights,
} from "@mui/icons-material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { ServerSideDataTable } from "../../components/common/DataTable";
import {
    guestOrderService,
    GuestOrderRequest,
    GuestOrderItem,
} from "../../services/guestOrderService";
import { orderChannelService } from "../../services/orderChannelService";
import { customerService } from "../../services/customerService";
import { formatCurrencyBDT } from "../../utils/currency";
import { formatDateShort } from "../../utils/date";
import { OrderForm, OrderFormPrefill } from "../../components/forms/OrderForm";
import { Order, Customer } from "../../types";
import { PAYMENT_METHOD_LABELS } from "../../utils/paymentMethods";
import { RequestSourceDetails, SourceCell } from "../../components/orders/RequestSource";
import { RequestSourcesCard } from "../../components/orders/RequestSourcesCard";

const STATUS_TABS = [
    "ALL",
    "PENDING",
    "PENDING_PAYMENT",
    "APPROVED",
    "REJECTED",
    "PAYMENT_FAILED",
] as const;
type StatusTab = (typeof STATUS_TABS)[number];

const statusColor = (
    status: GuestOrderRequest["status"],
): "warning" | "success" | "error" | "default" => {
    switch (status) {
        case "PENDING":
        case "PENDING_PAYMENT":
            return "warning";
        case "APPROVED":
            return "success";
        case "REJECTED":
        case "PAYMENT_FAILED":
            return "error";
        default:
            return "default";
    }
};

// Guest checkout only ever sends CASH/BKASH/NAGAD/CARD (see cps-ecom's
// checkout page), but this reuses the shared label source so it stays in
// sync automatically if a label ever changes centrally. CASH is
// overridden to "Cash on Delivery" — a guest never pays in advance, so the
// shared "Cash" label (which describes an already-in-hand payment) would
// be misleading here.
const paymentMethodLabel: Record<string, string> = {
    ...PAYMENT_METHOD_LABELS,
    CASH: "Cash on Delivery",
};

export const GuestOrderRequests: React.FC = () => {
    const [requests, setRequests] = useState<GuestOrderRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState("");
    const [actionSuccess, setActionSuccess] = useState("");
    // Tracks which single request's bKash recheck is in flight, so only
    // that row's button shows a spinner rather than the whole table.
    const [recheckingId, setRecheckingId] = useState<string | null>(null);

    const [activeTab, setActiveTab] = useState<StatusTab>("PENDING");
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });
    const [totalRows, setTotalRows] = useState(0);

    // Detail dialog
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] =
        useState<GuestOrderRequest | null>(null);

    // Reject dialog
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectTarget, setRejectTarget] = useState<GuestOrderRequest | null>(
        null,
    );
    const [rejectReason, setRejectReason] = useState("");

    // Bulk cleanup (e.g. spam submissions from an attack)
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({
        type: "include",
        ids: new Set(),
    });
    const [selectAllLoading, setSelectAllLoading] = useState(false);
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

    // OrderForm (approve) dialog
    const [orderFormOpen, setOrderFormOpen] = useState(false);
    const [orderFormPrefill, setOrderFormPrefill] =
        useState<OrderFormPrefill | null>(null);
    const [approveTarget, setApproveTarget] =
        useState<GuestOrderRequest | null>(null);
    // Every guest request arrived through the storefront, so approving one
    // should default the new order's channel to "Website Form".
    const [websiteFormChannelId, setWebsiteFormChannelId] = useState<
        string | undefined
    >(undefined);

    useEffect(() => {
        orderChannelService
            .getOrderChannelsSimple(true)
            .then((channels) => {
                const websiteForm = channels.find(
                    (c) => c.name === "Website Form",
                );
                setWebsiteFormChannelId(websiteForm?.order_channel_id);
            })
            .catch(() => {
                // Non-critical — the channel dropdown just stays unset.
            });
    }, []);

    // Bumped after each list load so the sources summary refreshes with it.
    const [loadCount, setLoadCount] = useState(0);

    const loadRequests = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const result = await guestOrderService.getRequests({
                skip: paginationModel.page * paginationModel.pageSize,
                limit: paginationModel.pageSize,
                status: activeTab === "ALL" ? undefined : activeTab,
            });
            setRequests(result.items);
            setTotalRows(result.total);
            setLoadCount((n) => n + 1);
        } catch (err: any) {
            setError(
                err?.response?.data?.detail || "Failed to load order requests",
            );
        } finally {
            setLoading(false);
        }
    }, [paginationModel, activeTab]);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    // Selection is tied to whatever status filter is showing — clear it on
    // tab switch so a "select all pending" doesn't linger while looking at
    // a different tab.
    useEffect(() => {
        setSelectedRows({ type: "include", ids: new Set() });
    }, [activeTab]);

    const handleSelectAllPending = async () => {
        setSelectAllLoading(true);
        setActionError("");
        try {
            const ids = await guestOrderService.getRequestIds("PENDING");
            setSelectedRows({ type: "include", ids: new Set(ids) });
        } catch (err: any) {
            setActionError(
                err?.response?.data?.detail ||
                    "Failed to load pending requests for selection",
            );
        } finally {
            setSelectAllLoading(false);
        }
    };

    const handleConfirmBulkDelete = async () => {
        setBulkDeleteLoading(true);
        setActionError("");
        setActionSuccess("");
        try {
            const ids = Array.from(selectedRows.ids) as string[];
            const result = await guestOrderService.bulkDelete(ids);
            setActionSuccess(
                result.skipped_ids.length > 0
                    ? `Deleted ${result.deleted_count} request(s). ${result.skipped_ids.length} were skipped (already approved/rejected, or not found).`
                    : `Deleted ${result.deleted_count} request(s).`,
            );
            setSelectedRows({ type: "include", ids: new Set() });
            setBulkDeleteOpen(false);
            loadRequests();
        } catch (err: any) {
            setActionError(
                err?.response?.data?.detail || "Failed to delete requests",
            );
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    // Build an OrderFormPrefill from a guest request and open the OrderForm dialog
    const handleOpenApprove = async (req: GuestOrderRequest) => {
        const items = guestOrderService.parseItems(req.items_snapshot);

        // If the guest was logged in at checkout, this request is already
        // linked to their account — fetch it so OrderForm pre-selects the
        // existing customer instead of creating a duplicate. If the fetch
        // fails for any reason, fall back to the ordinary new-customer
        // path rather than blocking approval.
        let existingCustomer: Customer | undefined;
        if (req.customer_id) {
            try {
                existingCustomer = await customerService.getCustomerById(
                    req.customer_id,
                );
            } catch (err) {
                console.error(
                    "Failed to fetch linked customer for guest request:",
                    err,
                );
            }
        }

        const prefill: OrderFormPrefill = {
            ...(existingCustomer
                ? { existing_customer: existingCustomer }
                : {
                      new_customer: {
                          first_name: req.first_name,
                          last_name: req.last_name || undefined,
                          email: req.email || undefined,
                          phone: req.phone,
                          street_address: req.street_address,
                          city: req.city,
                      },
                  }),
            items: items.map((it) => ({
                product_id: it.product_id,
                quantity: it.quantity,
                unit_price: it.unit_price,
            })),
            shipping_address: req.street_address,
            shipping_city: req.city,
            order_channel_id: websiteFormChannelId,
            // The area the customer picked at checkout, and the charge
            // they were shown for it.
            delivery_area_id: req.delivery_area_id || undefined,
            shipping_amount: req.shipping_amount,
            notes: req.notes
                ? `[E-commerce] Payment: ${req.payment_method}. ${req.notes}`
                : `[E-commerce] Payment preference: ${req.payment_method}`,
        };
        setApproveTarget(req);
        setOrderFormPrefill(prefill);
        setDetailOpen(false);
        setOrderFormOpen(true);
    };

    // Called by OrderForm after the real order is successfully created
    const handleOrderCreated = async (order: Order) => {
        if (!approveTarget) return;
        try {
            const result = await guestOrderService.linkOrder(
                approveTarget.request_id,
                order.order_id,
            );
            setActionSuccess(
                `Approved! Order ${result.order_number} created and linked to this request.`,
            );
            loadRequests();
        } catch (err: any) {
            // The real order was created — just the linking failed. Show a warning.
            setActionError(
                `Order created (${order.order_id}) but failed to mark request as approved: ` +
                    (err?.response?.data?.detail ||
                        err?.message ||
                        "Unknown error"),
            );
            loadRequests();
        } finally {
            setApproveTarget(null);
            setOrderFormPrefill(null);
        }
    };

    // For a PENDING_PAYMENT request stuck in an inconclusive state — asks
    // bKash directly for ground truth rather than guessing.
    const handleRecheckBkash = async (req: GuestOrderRequest) => {
        setRecheckingId(req.request_id);
        setActionError("");
        setActionSuccess("");
        try {
            const result = await guestOrderService.recheckBkashStatus(
                req.request_id,
            );
            setActionSuccess(
                result.pending
                    ? "Still processing — bKash hasn't confirmed this payment yet."
                    : result.message,
            );
            loadRequests();
        } catch (err: any) {
            setActionError(
                err?.response?.data?.detail ||
                    "Failed to recheck the bKash payment status",
            );
        } finally {
            setRecheckingId(null);
        }
    };

    const handleOpenReject = (req: GuestOrderRequest) => {
        setRejectTarget(req);
        setRejectReason("");
        setRejectOpen(true);
    };

    const handleConfirmReject = async () => {
        if (!rejectTarget) return;
        setActionLoading(true);
        setActionError("");
        setActionSuccess("");
        try {
            await guestOrderService.reject(
                rejectTarget.request_id,
                rejectReason,
            );
            setActionSuccess("Request rejected.");
            setRejectOpen(false);
            loadRequests();
        } catch (err: any) {
            setActionError(
                err?.response?.data?.detail || "Failed to reject request",
            );
        } finally {
            setActionLoading(false);
        }
    };

    const handleViewDetail = (req: GuestOrderRequest) => {
        setSelectedRequest(req);
        setDetailOpen(true);
    };

    const columns: GridColDef[] = [
        {
            field: "create_time",
            headerName: "Date",
            width: 130,
            renderCell: (params) => (
                <Typography variant="body2">
                    {formatDateShort(params.value)}
                </Typography>
            ),
        },
        {
            field: "name",
            headerName: "Customer",
            width: 160,
            renderCell: (params) => {
                const row = params.row as GuestOrderRequest;
                return (
                    <Box>
                        <Typography variant="body2" fontWeight={500}>
                            {row.first_name} {row.last_name || ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {row.phone}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: "source",
            headerName: "Source",
            width: 170,
            sortable: false,
            renderCell: (params) => <SourceCell source={(params.row as GuestOrderRequest).source} />,
        },
        {
            field: "city",
            headerName: "City",
            width: 110,
        },
        {
            field: "payment_method",
            headerName: "Payment",
            width: 140,
            renderCell: (params) => (
                <Typography variant="body2">
                    {paymentMethodLabel[params.value] || params.value}
                </Typography>
            ),
        },
        {
            field: "total_amount",
            headerName: "Total",
            width: 110,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight={600}>
                    {formatCurrencyBDT(params.value)}
                </Typography>
            ),
        },
        {
            field: "items_snapshot",
            headerName: "Items",
            width: 70,
            renderCell: (params) => {
                const items = guestOrderService.parseItems(params.value);
                return (
                    <Chip
                        label={items.length}
                        size="small"
                        icon={<ShoppingCart fontSize="small" />}
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: "status",
            headerName: "Status",
            width: 110,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color={statusColor(params.value)}
                />
            ),
        },
        {
            field: "request_actions",
            headerName: "Actions",
            width: 160,
            sortable: false,
            renderCell: (params) => {
                const row = params.row as GuestOrderRequest;
                return (
                    <Box display="flex" gap={0.5}>
                        <Tooltip title="View Details">
                            <IconButton
                                size="small"
                                onClick={() => handleViewDetail(row)}
                            >
                                <Visibility fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {row.status === "PENDING" && (
                            <>
                                <Tooltip title="Approve — open order form pre-filled with guest data">
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="success"
                                            onClick={() =>
                                                handleOpenApprove(row)
                                            }
                                        >
                                            <CheckCircle fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Reject">
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                handleOpenReject(row)
                                            }
                                        >
                                            <Cancel fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                        {row.status === "PENDING_PAYMENT" &&
                            row.payment_method === "BKASH" && (
                                <Tooltip title="Recheck bKash payment status">
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            disabled={
                                                recheckingId ===
                                                row.request_id
                                            }
                                            onClick={() =>
                                                handleRecheckBkash(row)
                                            }
                                        >
                                            {recheckingId ===
                                            row.request_id ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <Sync fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        {row.status === "APPROVED" &&
                            row.converted_order_id && (
                                <Chip
                                    label="Order created"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            )}
                    </Box>
                );
            },
        },
    ];

    const tabCounts = STATUS_TABS.map((tab) =>
        tab === "ALL"
            ? undefined
            : requests.filter((r) => r.status === tab).length,
    );

    return (
        <Box>
            {/* Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        E-commerce Order Requests
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Review and approve guest orders from the online store
                    </Typography>
                </Box>
                <Box display="flex" gap={1}>
                    <Button
                        startIcon={
                            selectAllLoading ? (
                                <CircularProgress size={16} />
                            ) : (
                                <PlaylistAddCheck />
                            )
                        }
                        onClick={handleSelectAllPending}
                        variant="outlined"
                        size="small"
                        disabled={selectAllLoading}
                    >
                        Select All Pending
                    </Button>
                    <Button
                        startIcon={<Refresh />}
                        onClick={loadRequests}
                        variant="outlined"
                        size="small"
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Bulk action toolbar — only visible when rows are selected */}
            {selectedRows.ids.size > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 2,
                        px: 2,
                        py: 1,
                        bgcolor: "action.selected",
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ mr: 1 }}>
                        {selectedRows.ids.size} request
                        {selectedRows.ids.size !== 1 ? "s" : ""} selected
                    </Typography>
                    <Button
                        size="small"
                        variant="contained"
                        color="error"
                        startIcon={<DeleteSweep />}
                        onClick={() => setBulkDeleteOpen(true)}
                    >
                        Delete Selected
                    </Button>
                </Box>
            )}

            {/* Feedback */}
            {actionSuccess && (
                <Alert
                    severity="success"
                    onClose={() => setActionSuccess("")}
                    sx={{ mb: 2 }}
                >
                    {actionSuccess}
                </Alert>
            )}
            {actionError && (
                <Alert
                    severity="error"
                    onClose={() => setActionError("")}
                    sx={{ mb: 2 }}
                >
                    {actionError}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Where requests came from (ads, sites, direct) */}
            <RequestSourcesCard refreshKey={loadCount} />

            {/* Status tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => {
                    setActiveTab(v);
                    setPaginationModel((p) => ({ ...p, page: 0 }));
                }}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
                {STATUS_TABS.map((tab, i) => (
                    <Tab
                        key={tab}
                        value={tab}
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                {tab}
                                {tabCounts[i] !== undefined &&
                                    tabCounts[i]! > 0 && (
                                        <Chip
                                            label={tabCounts[i]}
                                            size="small"
                                            color={
                                                tab === "PENDING"
                                                    ? "warning"
                                                    : "default"
                                            }
                                        />
                                    )}
                            </Box>
                        }
                    />
                ))}
            </Tabs>

            {/* Table */}
            <ServerSideDataTable
                rows={requests}
                columns={columns}
                loading={loading}
                hideActions={true}
                totalRows={totalRows}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                getRowId={(row) => row.request_id}
                checkboxSelection
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={setSelectedRows}
            />

            {/* Detail Dialog */}
            <Dialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Order Request Details
                    {selectedRequest && (
                        <Chip
                            label={selectedRequest.status}
                            size="small"
                            color={statusColor(selectedRequest.status)}
                            sx={{ ml: 1 }}
                        />
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {selectedRequest && (
                        <Box display="flex" flexDirection="column" gap={2}>
                            {/* Customer */}
                            <Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <Person fontSize="small" color="primary" />
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={600}
                                    >
                                        Customer
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    <strong>Name:</strong>{" "}
                                    {selectedRequest.first_name}{" "}
                                    {selectedRequest.last_name || ""}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Phone:</strong>{" "}
                                    {selectedRequest.phone}
                                </Typography>
                                {selectedRequest.email && (
                                    <Typography variant="body2">
                                        <strong>Email:</strong>{" "}
                                        {selectedRequest.email}
                                    </Typography>
                                )}
                            </Box>
                            <Divider />

                            {/* Shipping */}
                            <Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <LocalShipping
                                        fontSize="small"
                                        color="primary"
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={600}
                                    >
                                        Shipping Address
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    {selectedRequest.street_address},{" "}
                                    {selectedRequest.city}
                                    {selectedRequest.postal_code
                                        ? ` - ${selectedRequest.postal_code}`
                                        : ""}
                                </Typography>
                            </Box>
                            <Divider />

                            {/* Payment */}
                            <Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <Payment fontSize="small" color="primary" />
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={600}
                                    >
                                        Payment
                                    </Typography>
                                </Box>
                                <Typography variant="body2">
                                    {paymentMethodLabel[
                                        selectedRequest.payment_method
                                    ] || selectedRequest.payment_method}
                                </Typography>
                                {selectedRequest.bkash_trx_id && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        bKash transaction ID:{" "}
                                        {selectedRequest.bkash_trx_id}
                                    </Typography>
                                )}
                            </Box>
                            <Divider />

                            {/* Where they came from */}
                            {selectedRequest.source && (
                                <>
                                    <Box>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <Insights fontSize="small" color="primary" />
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                Where they came from
                                            </Typography>
                                        </Box>
                                        <RequestSourceDetails request={selectedRequest} />
                                    </Box>
                                    <Divider />
                                </>
                            )}

                            {/* Items */}
                            <Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <ShoppingCart
                                        fontSize="small"
                                        color="primary"
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={600}
                                    >
                                        Items
                                    </Typography>
                                </Box>
                                {guestOrderService
                                    .parseItems(selectedRequest.items_snapshot)
                                    .map(
                                        (item: GuestOrderItem, idx: number) => (
                                            <Box
                                                key={idx}
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                py={0.5}
                                            >
                                                <Box>
                                                    <Typography variant="body2">
                                                        {item.product_name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {formatCurrencyBDT(
                                                            item.unit_price,
                                                        )}{" "}
                                                        × {item.quantity}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                >
                                                    {formatCurrencyBDT(
                                                        item.total_price,
                                                    )}
                                                </Typography>
                                            </Box>
                                        ),
                                    )}
                                <Divider sx={{ my: 1 }} />
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Subtotal
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatCurrencyBDT(
                                            selectedRequest.subtotal_amount,
                                        )}
                                    </Typography>
                                </Box>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Shipping
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.shipping_amount === 0
                                            ? "Free"
                                            : formatCurrencyBDT(
                                                  selectedRequest.shipping_amount,
                                              )}
                                    </Typography>
                                </Box>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    mt={0.5}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={700}
                                    >
                                        Total
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={700}
                                    >
                                        {formatCurrencyBDT(
                                            selectedRequest.total_amount,
                                        )}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Notes */}
                            {selectedRequest.notes && (
                                <>
                                    <Divider />
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={600}
                                            mb={0.5}
                                        >
                                            Customer Notes
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {selectedRequest.notes}
                                        </Typography>
                                    </Box>
                                </>
                            )}

                            {/* Rejection reason */}
                            {selectedRequest.status === "REJECTED" &&
                                selectedRequest.rejection_reason && (
                                    <>
                                        <Divider />
                                        <Alert
                                            severity="error"
                                            variant="outlined"
                                        >
                                            <strong>Rejection reason:</strong>{" "}
                                            {selectedRequest.rejection_reason}
                                        </Alert>
                                    </>
                                )}

                            {/* Converted order */}
                            {selectedRequest.converted_order_id && (
                                <>
                                    <Divider />
                                    <Alert
                                        severity="success"
                                        variant="outlined"
                                    >
                                        <strong>Converted to order:</strong>{" "}
                                        {selectedRequest.converted_order_id}
                                    </Alert>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {selectedRequest?.status === "PENDING" && (
                        <>
                            <Button
                                color="success"
                                variant="contained"
                                startIcon={<CheckCircle />}
                                onClick={() =>
                                    handleOpenApprove(selectedRequest)
                                }
                            >
                                Approve & Create Order
                            </Button>
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<Cancel />}
                                onClick={() => {
                                    setDetailOpen(false);
                                    handleOpenReject(selectedRequest);
                                }}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                    <Button onClick={() => setDetailOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog
                open={rejectOpen}
                onClose={() => setRejectOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Reject Order Request</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Optionally provide a reason for rejection (for internal
                        records).
                    </Typography>
                    <TextField
                        label="Reason (optional)"
                        multiline
                        rows={3}
                        fullWidth
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="e.g. Out of stock, invalid address..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleConfirmReject}
                        disabled={actionLoading}
                        startIcon={
                            actionLoading ? (
                                <CircularProgress size={16} />
                            ) : (
                                <Cancel />
                            )
                        }
                    >
                        Confirm Reject
                    </Button>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Delete Confirmation Dialog */}
            <Dialog
                open={bulkDeleteOpen}
                onClose={() => !bulkDeleteLoading && setBulkDeleteOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete {selectedRows.ids.size} Request(s)?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        This permanently deletes these order requests. It
                        cannot be undone.
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        Only requests that never became a real order will
                        actually be removed (pending, awaiting bKash payment,
                        or a failed bKash payment) — anything already
                        approved or rejected is automatically skipped.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleConfirmBulkDelete}
                        disabled={bulkDeleteLoading}
                        startIcon={
                            bulkDeleteLoading ? (
                                <CircularProgress size={16} />
                            ) : (
                                <DeleteSweep />
                            )
                        }
                    >
                        Delete
                    </Button>
                    <Button
                        onClick={() => setBulkDeleteOpen(false)}
                        disabled={bulkDeleteLoading}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* OrderForm dialog — opened when admin clicks Approve */}
            <OrderForm
                open={orderFormOpen}
                onClose={() => {
                    setOrderFormOpen(false);
                    setOrderFormPrefill(null);
                    setApproveTarget(null);
                }}
                onSubmit={() => {}}
                onOrderCreated={handleOrderCreated}
                order={null}
                mode="create"
                prefill={orderFormPrefill}
            />
        </Box>
    );
};
